import React, { useState, useEffect } from 'react';
import {
	useFloating,
	useInteractions,
	useClick,
	useDismiss,
	useRole,
	autoUpdate,
	offset,
	flip,
	shift,
	FloatingFocusManager,
	FloatingPortal
} from '@floating-ui/react';
import { notificationStore } from 'components/layout/notifications/NotificationStore';
import AtsTooltip from 'ats/components/ats-tooltip/AtsTooltip';

const ActionMenu = ({
	id,
	buttonText,
	buttonIcon,
	buttonIconPosition = 'right',
	position = 'right',
	items = [],
	tooltipText,
	tooltipPosition = 'top',
	className = '',
	buttonClassName = '',
	menuClassName = ''
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [itemStates, setItemStates] = useState({});

	// Floating UI setup
	const { refs, floatingStyles, context } = useFloating({
		open: isOpen,
		onOpenChange: setIsOpen,
		middleware: [offset(2), flip(), shift()],
		whileElementsMounted: autoUpdate,
		placement: position === 'left' ? 'bottom-start' : 'bottom-end'
	});

	// Interaction hooks
	const click = useClick(context, {
		preventScroll: true
	});
	const dismiss = useDismiss(context);
	const role = useRole(context);

	const { getReferenceProps, getFloatingProps } = useInteractions([
		click,
		dismiss,
		role
	]);

	const handleMenuButtonClick = event => {
		event.stopPropagation();
		setIsOpen(prev => !prev);
	};

	const handleItemClick = (item, index) => {
		if (item.onClick) {
			try {
				item.onClick(id);

				// If there's a success state defined, update the item state
				if (item.successLabel || item.successIcon || item.successImg) {
					setItemStates(prev => ({
						...prev,
						[index]: { success: true }
					}));

					// Reset the state after the specified delay
					const delay = item.resetDelay !== undefined ? item.resetDelay : 2000;
					if (delay > 0) {
						setTimeout(() => {
							setItemStates(prev => {
								const newStates = { ...prev };
								delete newStates[index];
								return newStates;
							});
						}, delay);
					}
				}
			} catch (error) {
				console.error(error);
				notificationStore.pushErrorNotification(
					'Error',
					'An error occurred while executing the action'
				);
			}

			if (!item.successLabel && !item.successIcon && !item.successImg) {
				setIsOpen(false);
			}
		}
	};

	// Reset item states when menu closes
	useEffect(() => {
		if (!isOpen) {
			// Add a small delay before resetting to allow animations
			const timer = setTimeout(() => {
				setItemStates({});
			}, 300);
			return () => clearTimeout(timer);
		}
	}, [isOpen]);

	const getItemDisplay = (item, index) => {
		const state = itemStates[index];
		const isSuccess = state?.success;

		return {
			label: isSuccess && item.successLabel ? item.successLabel : item.label,
			icon: isSuccess && item.successIcon ? item.successIcon : item.icon,
			img: isSuccess && item.successImg ? item.successImg : item.img
		};
	};

	const menuButton = (
		<button
			className={`action-menu-button ${buttonClassName}`.trim()}
			ref={refs.setReference}
			{...getReferenceProps()}
			onClick={handleMenuButtonClick}
			type="button">
			{buttonIconPosition === 'left' ? buttonIcon || <>&#x25BC;</> : null}
			{buttonText ? <span>{buttonText}</span> : null}
			{buttonIconPosition === 'right' ? buttonIcon || <>&#x25BC;</> : null}
		</button>
	);

	return (
		<div
			className={`action-menu ${isOpen ? 'is-active' : ''} ${className}`.trim()}>
			{tooltipText ? (
				<AtsTooltip text={tooltipText} placement={tooltipPosition}>
					{menuButton}
				</AtsTooltip>
			) : (
				menuButton
			)}

			<FloatingPortal>
				{isOpen && (
					<FloatingFocusManager context={context} modal={false}>
						<ul
							ref={refs.setFloating}
							style={floatingStyles}
							{...getFloatingProps()}
							className={`action-menu-items ${position} is-active ${menuClassName}`.trim()}>
							{items?.map((item, index) => {
								const display = getItemDisplay(item, index);
								const isSuccess = itemStates[index]?.success;

								return (
									<li
										key={index}
										className={`action-menu-item ${isSuccess ? 'is-success' : ''}`}
										onClick={() => handleItemClick(item, index)}>
										{display.icon ? (
											<i className={`fas ${display.icon}`} />
										) : null}
										{display.img ? (
											<img alt={display.label} src={display.img} />
										) : null}
										{display.label}
									</li>
								);
							})}
						</ul>
					</FloatingFocusManager>
				)}
			</FloatingPortal>
		</div>
	);
};

export default ActionMenu;
